const BACKFILL_POLL_INTERVAL_MS = 5000;
const BACKFILL_POLL_TIMEOUT_MS = 10 * 60 * 1000;
const RUN_POLL_INTERVAL_MS = 5000;
const BACKFILL_TERMINAL_STATUSES = ['complete', 'failed'];
const RUN_TERMINAL_STATUSES = ['succeeded', 'failed_and_rolled_back', 'rolled_back', 'rollback_failed'];
const RUN_STATUS_LABEL_STATUSES = ['succeeded', 'failed_and_rolled_back', 'rolled_back', 'rollback_failed', 'rolling_back'];

const RUN_STAGES = [
  'validating',
  'building_episode_map',
  'b2b_inserts',
  'demographics_inserts',
  'dynamodb_merge',
  'hosting_stamp'
];

function globalFetch() {
  return window.fetch.bind(window);
}

function globalLocation() {
  return window.location;
}

function globalHistory() {
  return window.history;
}

/**
 * Escapes preview values before writing them into generated table rows.
 */
export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Formats persisted migration statuses and stages for admin display.
 */
export function titleize(value) {
  return String(value || 'Not started')
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Builds the Alpine state machine for the admin Prefix migration page.
 */
export function adminPrefixMigrationPage(options = {}) {
  const fetcher = options.fetch || globalFetch();
  const setTimeoutFn = options.setTimeout || window.setTimeout.bind(window);
  const clearTimeoutFn = options.clearTimeout || window.clearTimeout.bind(window);
  const nowFn = options.now || (() => Date.now());
  const location = options.location || globalLocation();
  const history = options.history || globalHistory();

  return {
    sourceShowId: '',
    sourceShowTitle: '',
    destPrefixShowId: '',
    destPrefixShowTitle: '',
    runId: '',
    runStatus: '',
    runStage: '',
    createUrl: '',
    previewUrl: '',
    backfillStartUrl: '',
    backfillStatusUrl: '',
    statusUrl: '',
    rollbackUrl: '',
    backfillShowId: '',
    backfillJid: '',
    backfillStartedAt: null,
    backfillTimer: null,
    runTimer: null,
    backfillLoading: false,
    previewLoading: false,
    submitting: false,
    rollbackSubmitting: false,
    previewLoaded: false,
    acceptUnmatched: false,
    confirmationTitle: '',
    rollbackConfirmationTitle: '',
    rollbackModalOpen: false,
    unmatchedEpisodes: [],
    skippedGuids: [],
    backfillMessage: 'Backfill status will appear here after the request starts.',
    flashMessage: '',
    flashMessageLevel: 'info',

    init() {
      const dataset = this.$root?.dataset || {};

      this.sourceShowId = dataset.sourceShowId || '';
      this.sourceShowTitle = dataset.sourceShowTitle || '';
      this.destPrefixShowId = dataset.destPrefixShowId || this.destInput()?.value || '';
      this.destPrefixShowTitle = dataset.destPrefixShowTitle || '';
      this.runId = dataset.runId || '';
      this.runStatus = dataset.runStatus || '';
      this.createUrl = dataset.createUrl || '';
      this.previewUrl = dataset.previewUrl || '';
      this.backfillStartUrl = dataset.backfillStartUrl || '';
      this.backfillStatusUrl = dataset.backfillStatusUrl || '';
      this.statusUrl = dataset.statusUrl || '';
      this.rollbackUrl = dataset.rollbackUrl || '';

      this.bindExistingControls();
      this.updateDestinationTitle();
      this.updateTitleWarning();

      if (this.shouldPollRunStatus) {
        this.scheduleRunPoll(0);
      }
    },

    get trimmedDestPrefixShowId() {
      return this.destPrefixShowId.trim();
    },

    get backfillDisabled() {
      return this.backfillLoading || this.previewLoading || this.submitting || !!this.runId;
    },

    get backfillButtonLabel() {
      if (this.backfillLoading) return 'Backfill running...';
      if (this.previewLoading) return 'Loading preview...';
      return 'Run feed backfill on prefix';
    },

    get unmatchedAccepted() {
      if (this.unmatchedEpisodes.length === 0) return true;
      return this.acceptUnmatched && this.missingSkippedGuids().length === 0;
    },

    get canMigrate() {
      return !this.runId &&
        this.previewLoaded &&
        !this.previewLoading &&
        !this.submitting &&
        this.trimmedDestPrefixShowId.length > 0 &&
        this.confirmationTitle.trim() === this.sourceShowTitle &&
        this.unmatchedAccepted;
    },

    get canConfirmRollback() {
      return !this.rollbackSubmitting &&
        this.rollbackConfirmationTitle.trim() === this.sourceShowTitle;
    },

    get titleMismatch() {
      return this.destPrefixShowTitle.trim().length > 0 &&
        this.sourceShowTitle.trim().length > 0 &&
        this.destPrefixShowTitle !== this.sourceShowTitle;
    },

    get flashMessageClass() {
      const base = 'rounded-md border-l-4 px-4 py-3 text-sm shadow-sm';
      if (this.flashMessageLevel === 'error') {
        return `${base} border-red-400 border bg-red-50 text-red-800`;
      }
      if (this.flashMessageLevel === 'success') {
        return `${base} border-green-400 border bg-green-50 text-green-800`;
      }
      return `${base} border-blue-400 border bg-blue-50 text-blue-800`;
    },

    get shouldPollRunStatus() {
      return !!this.runId &&
        !!this.statusUrl &&
        !RUN_TERMINAL_STATUSES.includes(this.runStatus);
    },

    resetPreview() {
      this.previewLoaded = false;
      this.previewLoading = false;
      this.acceptUnmatched = false;
      this.unmatchedEpisodes = [];
      this.skippedGuids = [];
      this.destPrefixShowTitle = '';
      this.backfillShowId = '';
      this.backfillJid = '';
      this.backfillStartedAt = null;
      this.backfillMessage = 'Backfill status will appear here after the request starts.';
      this.clearBackfillTimer();
      this.renderPreviewPlaceholder('Preview results will appear here after the prefix feed backfill completes.');
      this.updateAcceptCheckboxState();
      this.updateDestinationTitle();
      this.updateTitleWarning();
    },

    async startBackfill() {
      this.clearFlashMessage();

      if (!this.trimmedDestPrefixShowId) {
        this.backfillMessage = 'Destination Prefix show ID is required.';
        this.showFlashMessage('Destination Prefix show ID is required.', 'error');
        return;
      }

      this.backfillLoading = true;
      this.previewLoaded = false;
      this.renderPreviewPlaceholder('Waiting for prefix feed backfill to complete...');
      this.backfillMessage = 'Queued prefix feed backfill...';

      try {
        const payload = await this.requestJson(this.backfillStartUrl, {
          method: 'POST',
          body: {
            dest_prefix_show_id: this.trimmedDestPrefixShowId
          }
        });

        this.backfillShowId = payload.backfill_show_id || payload.show_id || '';
        this.backfillJid = payload.jid || '';
        this.backfillStartedAt = nowFn();
        this.backfillMessage = 'Prefix feed backfill is queued.';
        this.scheduleBackfillPoll(0);
      } catch (error) {
        this.backfillLoading = false;
        this.backfillMessage = error.message;
        this.showFlashMessage(error.message, 'error');
      }
    },

    async pollBackfillStatus() {
      if (!this.backfillShowId || !this.backfillJid) return;

      if (this.backfillTimedOut()) {
        this.backfillLoading = false;
        this.backfillMessage = 'Prefix feed backfill timed out before completing.';
        this.showFlashMessage(this.backfillMessage, 'error');
        return;
      }

      try {
        const payload = await this.requestJson(
          this.urlWithParams(this.backfillStatusUrl, {
            backfill_show_id: this.backfillShowId,
            jid: this.backfillJid
          })
        );

        await this.handleBackfillStatus(payload);
      } catch (error) {
        if (error.status === 404 && !this.backfillTimedOut()) {
          this.backfillMessage = 'Prefix feed backfill has not started yet.';
          this.scheduleBackfillPoll();
          return;
        }

        this.backfillLoading = false;
        this.backfillMessage = error.message;
        this.showFlashMessage(error.message, 'error');
      }
    },

    async handleBackfillStatus(payload) {
      const status = payload.status;

      if (status === 'not_started') {
        this.backfillMessage = 'Prefix feed backfill has not started yet.';
        this.scheduleBackfillPoll();
        return;
      }

      if (!BACKFILL_TERMINAL_STATUSES.includes(status)) {
        this.backfillMessage = 'Prefix feed backfill is running.';
        this.scheduleBackfillPoll();
        return;
      }

      this.backfillLoading = false;

      if (status === 'failed') {
        this.backfillMessage = payload.error || 'Prefix feed backfill failed.';
        this.showFlashMessage(this.backfillMessage, 'error');
        return;
      }

      this.backfillMessage = 'Prefix feed backfill complete. Loading match preview...';
      await this.loadPreview();
    },

    async loadPreview() {
      this.previewLoading = true;

      try {
        const payload = await this.requestJson(
          this.urlWithParams(this.previewUrl, {
            dest_prefix_show_id: this.trimmedDestPrefixShowId
          })
        );

        this.destPrefixShowTitle = payload.dest_title || '';
        this.unmatchedEpisodes = Array.isArray(payload.unmatched) ? payload.unmatched : [];
        this.skippedGuids = this.unmatchedEpisodes.map((episode) => String(episode.guid ?? ''));
        this.acceptUnmatched = this.unmatchedEpisodes.length === 0;
        this.previewLoaded = true;
        this.previewLoading = false;
        this.backfillMessage = 'Match preview loaded.';
        this.renderPreview(payload);
        this.updateAcceptCheckboxState();
        this.updateDestinationTitle();
        this.updateTitleWarning();
      } catch (error) {
        this.previewLoading = false;
        this.backfillMessage = error.message;
        this.showFlashMessage(error.message, 'error');
      }
    },

    async submitMigration() {
      this.clearFlashMessage();

      if (!this.canMigrate) {
        this.showFlashMessage('Complete the backfill, preview, acknowledgement, and title confirmation before migrating.', 'error');
        return;
      }

      this.submitting = true;

      try {
        const payload = await this.requestJson(this.createUrl, {
          method: 'POST',
          body: {
            dest_prefix_show_id: this.trimmedDestPrefixShowId,
            skipped_guids: this.skippedGuids,
            confirmation_title: this.confirmationTitle.trim()
          }
        });

        this.applyRunPayload(payload);
        this.statusUrl = this.runStatusUrl(payload.id);
        this.rollbackUrl = this.runRollbackUrl(payload.id);
        this.replaceBrowserUrl(this.runShowUrl(payload.id));
        this.scheduleRunPoll(0);
      } catch (error) {
        this.showFlashMessage(error.message, 'error');
      } finally {
        this.submitting = false;
      }
    },

    async pollRunStatus() {
      if (!this.statusUrl) return;

      try {
        const payload = await this.requestJson(this.statusUrl);
        this.applyRunPayload(payload);

        if (RUN_TERMINAL_STATUSES.includes(this.runStatus)) {
          this.reloadCurrentPage();
          return;
        }

        this.scheduleRunPoll();
      } catch (error) {
        this.showFlashMessage(error.message, 'error');
        this.scheduleRunPoll();
      }
    },

    openRollbackModal(url = null, title = null) {
      this.rollbackUrl = url || this.rollbackUrl;
      this.rollbackConfirmationTitle = '';
      this.rollbackModalOpen = true;

      if (title) {
        this.sourceShowTitle = title;
      }
    },

    closeRollbackModal() {
      this.rollbackModalOpen = false;
      this.rollbackConfirmationTitle = '';
    },

    async rollbackRun() {
      if (!this.canConfirmRollback || !this.rollbackUrl) return;

      this.rollbackSubmitting = true;
      this.clearFlashMessage();

      try {
        const payload = await this.requestJson(this.rollbackUrl, {
          method: 'POST',
          body: {
            rollback_confirmation_title: this.rollbackConfirmationTitle.trim()
          }
        });
        this.applyRunPayload(payload);
        this.reloadCurrentPage();
      } catch (error) {
        this.showFlashMessage(error.message, 'error');
      } finally {
        this.rollbackSubmitting = false;
      }
    },

    applyRunPayload(payload) {
      this.runId = String(payload.id || this.runId || '');
      this.runStatus = payload.status || this.runStatus || '';
      this.runStage = payload.stage || this.runStage || '';
      this.destPrefixShowTitle = payload.dest_prefix_show_title || this.destPrefixShowTitle || '';
      this.updateStatusPill();
      this.updateProgress(payload);
      this.updateDestinationTitle();
      this.updateTitleWarning();
    },

    bindExistingControls() {
      const acceptCheckbox = this.acceptCheckbox();
      if (acceptCheckbox) {
        acceptCheckbox.addEventListener('change', (event) => {
          this.acceptUnmatched = event.target.checked;
        });
      }

      const rollbackButton = this.rollbackButton();
      if (rollbackButton) {
        rollbackButton.addEventListener('click', () => {
          this.openRollbackModal(
            rollbackButton.dataset.rollbackUrl,
            rollbackButton.dataset.confirmationTitle
          );
        });
      }

      this.updateAcceptCheckboxState();
    },

    bindSkipCheckboxes() {
      this.$root.querySelectorAll('[data-prefix-migration-skip-guid]').forEach((checkbox) => {
        checkbox.addEventListener('change', (event) => {
          this.setSkippedGuid(event.target.value, event.target.checked);
        });
      });
    },

    setSkippedGuid(guid, checked) {
      const normalizedGuid = String(guid ?? '');

      if (checked) {
        if (!this.skippedGuids.includes(normalizedGuid)) {
          this.skippedGuids.push(normalizedGuid);
        }
        return;
      }

      this.skippedGuids = this.skippedGuids.filter((value) => value !== normalizedGuid);
    },

    missingSkippedGuids() {
      const skipped = new Set(this.skippedGuids.map((guid) => String(guid ?? '')));
      return this.unmatchedEpisodes
        .map((episode) => String(episode.guid ?? ''))
        .filter((guid) => !skipped.has(guid));
    },

    renderPreview(payload) {
      const body = this.previewBody();
      if (!body) return;

      if (this.unmatchedEpisodes.length === 0) {
        body.innerHTML = `
          <tr data-prefix-migration-empty-preview-row="true">
            <td colspan="4" class="px-5 py-8 text-center text-sm text-gray-500">
              All ${escapeHtml(payload.hosting_episode_count || 0)} hosting episodes have matching active Prefix episodes.
            </td>
          </tr>
        `;
        return;
      }

      body.innerHTML = this.unmatchedEpisodes.map((episode) => this.renderUnmatchedRow(episode)).join('');
      this.bindSkipCheckboxes();
    },

    renderUnmatchedRow(episode) {
      const guid = String(episode.guid ?? '');
      const title = episode.title || '-';
      const pubDate = episode.pub_date || '-';

      return `
        <tr data-prefix-migration-unmatched-row="true">
          <td class="px-5 py-4">
            <input type="checkbox"
                   name="skipped_guids[]"
                   value="${escapeHtml(guid)}"
                   checked
                   class="rounded border-gray-300 text-purple-600 shadow-sm focus:ring-purple-500"
                   data-prefix-migration-skip-guid="${escapeHtml(guid)}">
          </td>
          <td class="whitespace-nowrap px-5 py-4 text-sm text-gray-500">${escapeHtml(title)}</td>
          <td class="break-all px-5 py-4 text-sm text-gray-900">${escapeHtml(guid || '(blank guid)')}</td>
          <td class="whitespace-nowrap px-5 py-4 text-sm text-gray-500">${escapeHtml(pubDate)}</td>
        </tr>
      `;
    },

    renderPreviewPlaceholder(message) {
      const body = this.previewBody();
      if (!body) return;

      body.innerHTML = `
        <tr data-prefix-migration-empty-preview-row="true">
          <td colspan="4" class="px-5 py-8 text-center text-sm text-gray-500">
            ${escapeHtml(message)}
          </td>
        </tr>
      `;
    },

    updateAcceptCheckboxState() {
      const checkbox = this.acceptCheckbox();
      if (!checkbox) return;

      checkbox.disabled = this.unmatchedEpisodes.length === 0;
      checkbox.checked = this.acceptUnmatched && this.unmatchedEpisodes.length > 0;
    },

    updateDestinationTitle() {
      const title = this.destinationTitle();
      if (!title) return;

      title.textContent = this.destPrefixShowTitle || 'Enter a destination Prefix show ID below.';
    },

    updateTitleWarning() {
      const warning = this.titleWarning();
      if (!warning) return;

      warning.style.display = this.titleMismatch ? '' : 'none';
    },

    updateStatusPill() {
      const pill = this.statusPill();
      if (!pill || !this.runStatus) return;

      pill.textContent = titleize(this.runStatus);
    },

    updateProgress(payload) {
      const stage = payload.stage || null;
      const status = payload.status || null;

      const stageEl = this.stageElement();
      if (stageEl) {
        const label = RUN_STATUS_LABEL_STATUSES.includes(status) ? status : (stage || status || 'Not started');
        stageEl.textContent = titleize(label);
      }

      const progressBar = this.progressBar();
      if (progressBar) {
        progressBar.style.width = `${this.progressPercent(status, stage)}%`;
      }

      this.updateCount('b2b', payload.b2b_rows_migrated);
      this.updateCount('demographics', payload.demographics_rows_migrated);
      this.updateCount('dynamodb', payload.dynamodb_rows_merged);
    },

    updateCount(kind, value) {
      const count = this.$root.querySelector(`[data-prefix-migration-count="${kind}"]`);
      if (count) {
        count.textContent = Number(value || 0).toString();
      }
    },

    progressPercent(status, stage) {
      if (status === 'succeeded') {
        return 100;
      }

      if (status === 'rolled_back' || status === 'failed_and_rolled_back' || status === 'rollback_failed') {
        return 0;
      }

      if (status === 'pending') {
        return 5;
      }

      const index = RUN_STAGES.indexOf(stage);
      if (status === 'running' && index >= 0) {
        return Math.round(((index + 1) / RUN_STAGES.length) * 100);
      }

      return 0;
    },

    async requestJson(url, options = {}) {
      const response = await fetcher(url, this.fetchOptions(options));
      const payload = await this.parseJsonResponse(response);

      if (!response.ok) {
        const error = new Error(payload.error || `Request failed with HTTP ${response.status}`);
        error.status = response.status;
        error.payload = payload;
        throw error;
      }

      return payload;
    },

    async parseJsonResponse(response) {
      try {
        return await response.json();
      } catch (_error) {
        return {};
      }
    },

    fetchOptions(options) {
      const method = options.method || 'GET';
      const headers = {
        Accept: 'application/json'
      };

      if (method !== 'GET') {
        headers['Content-Type'] = 'application/json';
        headers['X-CSRF-Token'] = this.csrfToken();
      }

      const requestOptions = {
        method,
        headers,
        credentials: 'same-origin'
      };

      if (options.body) {
        requestOptions.body = JSON.stringify(options.body);
      }

      return requestOptions;
    },

    csrfToken() {
      const meta = document.querySelector('meta[name="csrf-token"]');
      return meta?.content || meta?.getAttribute('content') || '';
    },

    urlWithParams(url, params) {
      const query = new URLSearchParams(params);
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}${query.toString()}`;
    },

    scheduleBackfillPoll(delay = BACKFILL_POLL_INTERVAL_MS) {
      this.clearBackfillTimer();
      this.backfillTimer = setTimeoutFn(() => this.pollBackfillStatus(), delay);
    },

    clearBackfillTimer() {
      if (this.backfillTimer) {
        clearTimeoutFn(this.backfillTimer);
        this.backfillTimer = null;
      }
    },

    scheduleRunPoll(delay = RUN_POLL_INTERVAL_MS) {
      this.clearRunTimer();
      this.runTimer = setTimeoutFn(() => this.pollRunStatus(), delay);
    },

    clearRunTimer() {
      if (this.runTimer) {
        clearTimeoutFn(this.runTimer);
        this.runTimer = null;
      }
    },

    backfillTimedOut() {
      return this.backfillStartedAt !== null &&
        nowFn() - this.backfillStartedAt >= BACKFILL_POLL_TIMEOUT_MS;
    },

    showFlashMessage(message, level = 'info') {
      this.flashMessage = message;
      this.flashMessageLevel = level;
    },

    clearFlashMessage() {
      this.flashMessage = '';
      this.flashMessageLevel = 'info';
    },

    runShowUrl(id) {
      return `${this.createUrl.replace(/\/$/, '')}/${id}`;
    },

    runStatusUrl(id) {
      return `${this.runShowUrl(id)}/status`;
    },

    runRollbackUrl(id) {
      return `${this.runShowUrl(id)}/rollback`;
    },

    replaceBrowserUrl(url) {
      if (history?.replaceState) {
        history.replaceState({}, '', url);
      }
    },

    reloadCurrentPage() {
      if (location?.reload) {
        location.reload();
      }
    },

    destInput() {
      return this.$root.querySelector('[data-prefix-migration-dest-input]');
    },

    previewBody() {
      return this.$root.querySelector('[data-prefix-migration-match-preview-body]');
    },

    acceptCheckbox() {
      return this.$root.querySelector('[data-prefix-migration-accept-unmatched]');
    },

    rollbackButton() {
      return this.$root.querySelector('[data-prefix-migration-rollback-button]');
    },

    destinationTitle() {
      return this.$root.querySelector('[data-prefix-migration-dest-title]');
    },

    titleWarning() {
      return this.$root.querySelector('[data-prefix-migration-title-warning]');
    },

    statusPill() {
      return this.$root.querySelector('[data-prefix-migration-status-pill]');
    },

    stageElement() {
      return this.$root.querySelector('[data-prefix-migration-stage]');
    },

    progressBar() {
      return this.$root.querySelector('[data-prefix-migration-progress-bar]');
    }
  };
}

document.addEventListener('alpine:init', () => {
  Alpine.data('admin_prefix_migration_page', () => adminPrefixMigrationPage());
});
